import {
  AppContext,
  IDP,
  idpPath,
  Loader,
  Logout as LogOut,
  usePermission
} from '@ctsu/loader';
import { GoogleAnalytics } from '@ctsu/react-ctsu-ui';
import { createBrowserHistory } from 'history';
import React, {
  cloneElement,
  Suspense,
  useContext,
  useEffect
} from 'react';
import { Provider } from 'react-redux';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useNavigate,
  useSearchParams
} from 'react-router-dom';
import {
  DEFAULT_REQUEST,
  LOGIN_PATH,
  LOGOUT_PATH,
  TEMPLATE_BROWSER_PATH
} from 'routes/constants';
import AppContent from './AppContent';
import DocumentTitle from './components/common/DocumentTitle';
import Permission from './components/common/Permission';
import HelpTopics from './components/HelpTopics';
import Login, { GotoLogin } from './components/Login';
import { flattenRoutes, getGAId } from './helper/utils';
import appRoutes from './routes/appRoutes';
import store from './stores';

function Loading() {
  return <div>...Loading</div>;
}
function NavigateWithParam({ to }) {
  const [searchParams] = useSearchParams();
  return <Navigate to={`${to}?${searchParams}`} replace={true} />;
}

function Logout() {
  useEffect(() => {
    sessionStorage.clear();
    localStorage.clear();
  }, []);
  const referrer = { referrer: { pathname: '/' } };
  // Replace the refferer with default path when log out so the default page logic will take place the next time you login
  return (
    <LogOut
      logout={<Navigate to={LOGIN_PATH} replace={true} state={referrer} />}
    />
  );
}
function Refresh() {
  const navigate = useNavigate();
  useEffect(() => {
    const timerId = setTimeout(() => navigate(-1), 20);
    return () => clearTimeout(timerId);
  });
  return null;
}

function Protect() {
  const context = useContext(AppContext);
  const { session } = context;
  const authenticated = session?.username;
  if (authenticated) {
    return <Permission />;
  }
  return <GotoLogin />;
}

function ProtectedRoute({
  permission,
  title,
  helpContext,
  element
}) {
  const hasPermit = usePermission(permission);
  if (hasPermit) {
    return (
      <div id="app-context" data-permission={permission}>
        <DocumentTitle title={title} />
        <HelpTopics contextKey={helpContext} />
        {cloneElement(element, {})}
      </div>
    );
  }

  return (
    <>
      <DocumentTitle title="Access Denied" />
      <Protect />
    </>
  );
}

const flatRoutes = flattenRoutes(appRoutes);

export default function APP() {
  const history = createBrowserHistory();

  return (
    <Provider store={store}>
      <Suspense
        fallback={
          <main>
            <Loading />
          </main>
        }
      >
        <Loader loadIndicator={<Loading />}>
          <BrowserRouter history={history}>
            <AppContent>
              <Routes>
                {flatRoutes.map(
                  ({
                    path,
                    exact,
                    permission,
                    title,
                    helpContext,
                    element
                  }) => (
                    <Route
                      key={path}
                      exact={exact}
                      path={path}
                      element={
                        <ProtectedRoute
                          {...{
                            permission,
                            title,
                            helpContext,
                            element
                          }}
                        />
                      }
                    />
                  )
                )}
                {/* redirect from old Template Browser path */}
                <Route
                  path="/TemplateBrowser"
                  element={<NavigateWithParam to={TEMPLATE_BROWSER_PATH} />}
                />
                <Route path="/temp" element={<Refresh />} />
                <Route path={idpPath} element={<IDP />} />
                <Route exact path={LOGOUT_PATH} element={<Logout />} />
                <Route path={LOGIN_PATH} element={<Login />} />
                <Route
                  exact
                  path="/"
                  element={<Navigate to={DEFAULT_REQUEST} />}
                />
                <Route path="" element={<Navigate to={LOGIN_PATH} />} />
              </Routes>
            </AppContent>
            <GoogleAnalytics
              application={process.env.REACT_APP_NAME}
              gaTrackingId={getGAId()}
            />
          </BrowserRouter>
        </Loader>
      </Suspense>
    </Provider>
  );
}
